import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import Styles from './AddGroupUsersMoal.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { getAllChatUserActionByNumber, getAllChatUserActionBySearch } from '../../../../redux/actions/chatActions';

const AV_COLORS = ['#800020', '#7c3aed', '#065f46', '#b45309', '#1d4ed8', '#9d174d'];
const getAvColor = (name) => AV_COLORS[(name?.charCodeAt(0) || 0) % AV_COLORS.length];
const getInitials = (name) => {
	if (!name) return '?';
	const parts = name.trim().split(' ');
	return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : parts[0].slice(0, 2).toUpperCase();
};

const AddGroupUsersMoal = ({ socket, acceptedUsers, showModal, setShowModal }) => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const [circularLoader, setCircularLoader] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedItems, setSelectedItems] = useState([]);
	const [searchResults, setSearchResults] = useState([]);
	const [hasMoreData, setHasMoreData] = useState(true);
	const [page, setPage] = useState(2);
	const [inputGrouNameValue, setInputGrouNameValue] = useState('');
	const [isSearching, setIsSearching] = useState(false);
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const debounceTimeoutRef = useRef(null);
	const cardListRef = useRef(null);
	const pageSize = 10;

	const dispatch = useDispatch();
	const userLogin = useSelector((state) => state.userLogin);
	const onlineUsers = useSelector((state) => state.onlineUsers);
	const isButtonDisabled = selectedItems.length === 0 || inputGrouNameValue.trim().length === 0;

	useEffect(() => { setSearchResults(acceptedUsers); }, [acceptedUsers]);

	const onClose = useCallback(() => {
		setShowModal(false);
		setLoading(false);
		setSearchResults(acceptedUsers);
		setSelectedItems([]);
		setSearchQuery('');
		setInputGrouNameValue('');
	}, [acceptedUsers, setShowModal]);

	const search = useCallback(async (query) => {
		setIsSearching(query.length > 0);
		if (query.length > 0) {
			setCircularLoader(true);
			const filteredData = acceptedUsers.filter((item) =>
				item.name.toLowerCase().includes(query.toLowerCase()) ||
				item.email.toLowerCase().includes(query.toLowerCase())
			);
			setSearchResults(filteredData);
			const data = await dispatch(getAllChatUserActionBySearch('fresh', query));
			setCircularLoader(false);
			setSearchResults(data.data);
		} else {
			setIsSearching(false);
			setSearchResults(acceptedUsers);
		}
	}, [acceptedUsers, dispatch]);

	useEffect(() => { search(debouncedSearch); }, [debouncedSearch, search]);

	const handleCheckboxChange = useCallback((item) => {
		setSelectedItems((prev) =>
			prev.some((s) => s.id === item.id)
				? prev.filter((s) => s.id !== item.id)
				: [...prev, item]
		);
	}, []);

	const handleInviteSubmit = useCallback((e) => {
		e.preventDefault();
		const userData = Object.values(onlineUsers.onlineUsers).find(
			(u) => u['userId'] === userLogin.userInfo.data.user._id
		);
		const orgId = userLogin.userInfo.data.user.org;
		socket.emit('createGroup', {
			members: selectedItems,
			senderId: userData.userId,
			org: orgId,
			mySocketId: userData.socketId,
			groupName: inputGrouNameValue,
		});
		setLoading(true);
		setTimeout(() => onClose(), 1000);
	}, [onlineUsers, selectedItems, socket, userLogin, inputGrouNameValue, onClose]);

	const fetchData = useCallback(async () => {
		if (!circularLoader && hasMoreData && !isSearching) {
			setCircularLoader(true);
			const data = await dispatch(getAllChatUserActionByNumber('fresh', page));
			const newData = data.data;
			if (newData.length > 0) {
				setSearchResults((prev) => [...prev, ...newData]);
				setPage((p) => p + 1);
			} else {
				setHasMoreData(false);
			}
			setCircularLoader(false);
		}
	}, [circularLoader, dispatch, hasMoreData, isSearching, page]);

	const handleScroll = useCallback(() => {
		const el = cardListRef.current;
		if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 4 && hasMoreData) {
			fetchData();
		}
	}, [fetchData, hasMoreData]);

	useEffect(() => {
		const el = cardListRef.current;
		if (el) el.addEventListener('scroll', handleScroll);
		return () => { if (el) el.removeEventListener('scroll', handleScroll); };
	}, [handleScroll]);

	if (!showModal) return null;

	return ReactDOM.createPortal(
		<div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
			<div className={Styles.modal}>
				{/* Header */}
				<div className={Styles.modalHeader}>
					<div className={Styles.modalHeaderLeft}>
						<div className={Styles.modalIconBg}>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
								<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
								<circle cx="9" cy="7" r="4" />
								<path d="M23 21v-2a4 4 0 0 0-3-3.87" />
								<path d="M16 3.13a4 4 0 0 1 0 7.75" />
							</svg>
						</div>
						<span className={Styles.modalTitle}>{t('screens.chat.newGroupChat')}</span>
					</div>
					<button className={Styles.closeBtn} onClick={onClose} title={t('screens.chat.close')}>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
							<line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
						</svg>
					</button>
				</div>

				{/* Group name input */}
				<div className={Styles.nameSection}>
					<label className={Styles.nameLabel}>{t('screens.chat.groupName')}</label>
					<input
						autoFocus
						className={Styles.nameInput}
						placeholder={t('screens.chat.groupNamePh')}
						value={inputGrouNameValue}
						onChange={(e) => setInputGrouNameValue(e.target.value)}
					/>
				</div>

				{/* Search */}
				<div className={Styles.searchSection}>
					<div className={Styles.searchWrap}>
						<svg className={Styles.searchIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
						</svg>
						<input
							className={Styles.searchInput}
							placeholder={t('screens.chat.searchColleagues')}
							value={searchQuery}
							onChange={(e) => {
								setSearchQuery(e.target.value);
								clearTimeout(debounceTimeoutRef.current);
								debounceTimeoutRef.current = setTimeout(() => {
									setDebouncedSearch(e.target.value);
								}, 150);
							}}
						/>
					</div>
				</div>

				{/* User list */}
				{acceptedUsers && acceptedUsers.length > 0 ? (
					<div className={Styles.list} ref={cardListRef}>
						{searchResults?.map((item) => {
							const isSelected = selectedItems.some((s) => s.id === item._id);
							return (
								<div
									key={item._id}
									className={`${Styles.userRow} ${isSelected ? Styles.userRowSelected : ''}`}
									onClick={() => handleCheckboxChange({ name: item.name, email: item.email, id: item._id })}
								>
									<div className={Styles.userAv} style={{ background: getAvColor(item.name) }}>
										{getInitials(item.name)}
									</div>
									<div className={Styles.userInfo}>
										<span className={Styles.userName}>{item.name}</span>
										<span className={Styles.userEmail}>{item.email}</span>
									</div>
									<div className={`${Styles.checkbox} ${isSelected ? Styles.checkboxChecked : ''}`}>
										{isSelected && (
											<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
												<polyline points="20 6 9 17 4 12" />
											</svg>
										)}
									</div>
								</div>
							);
						})}
						{circularLoader && (
							<div className={Styles.loadingRow}>
								<div className={Styles.spinner} />
								<span>{t('screens.chat.loading')}</span>
							</div>
						)}
					</div>
				) : (
					<div className={Styles.emptyList}>
						<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
							<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
							<circle cx="9" cy="7" r="4" />
						</svg>
						<span>{t('screens.chat.noColleagues')}</span>
					</div>
				)}

				{/* Footer */}
				{acceptedUsers && acceptedUsers.length > 0 && (
					<div className={Styles.footer}>
						<span className={Styles.selectedCount}>
							{selectedItems.length > 0 ? t('screens.chat.membersSelected', { count: selectedItems.length }) : t('screens.chat.selectMembersAbove')}
						</span>
						<button
							className={Styles.createBtn}
							onClick={handleInviteSubmit}
							disabled={isButtonDisabled}
						>
							{loading ? t('screens.chat.creating') : t('screens.chat.createGroup')}
						</button>
					</div>
				)}
			</div>
		</div>,
		document.body
	);
};

export default AddGroupUsersMoal;
